import { TsDOM as q } from '@/scripts/core/TsDOM'
import { UcoastEl } from '@/scripts/core/UcoastEl'
import { replaceProductUrlParameters } from '@/scripts/product/product-url'

export class SubscriptionWidget extends UcoastEl {
	static htmlSelector = 'subscription-widget'

	containers: NodeListOf<HTMLElement>
	sellingPlanSelected: HTMLInputElement
	onetimeInput?: HTMLInputElement
	subscriptions: NodeListOf<HTMLInputElement>
	sellingPlanToggles: NodeListOf<HTMLButtonElement>
	sellingPlanSelects: NodeListOf<HTMLElement>
	sellingPlans: NodeListOf<HTMLElement>
	sellingPlanContents: NodeListOf<HTMLElement>
	updatesUrl: boolean

	constructor() {
		super()
		this.containers = q.rl<HTMLElement>(
			'.subscription-widget__container',
			this
		)
		this.sellingPlanSelected = q.rs<HTMLInputElement>(
			'.selling_plan_input',
			this
		)
		this.onetimeInput = q.os<HTMLInputElement>(
			'.subscription-widget__container .onetime',
			this
		)
		this.subscriptions = q.rl<HTMLInputElement>(
			'.subscription-widget__container .subscription',
			this
		)
		this.sellingPlanToggles = q.rl<HTMLButtonElement>(
			'.product-subscription__select .selling-plan-selected',
			this
		)
		this.sellingPlanSelects = q.rl<HTMLElement>(
			'.product-subscription__select',
			this
		)
		this.sellingPlans = q.rl<HTMLElement>(
			'.product-subscription__select .selling-plan',
			this
		)
		this.sellingPlanContents = q.rl<HTMLElement>(
			'.subscription-widget__content',
			this
		)
		this.updatesUrl = !this.closest('[data-update-url="false"]')

		this.subscriptions.forEach((subscriptionInput) => {
			subscriptionInput.addEventListener('change', () => {
				if (!subscriptionInput.checked) return
				this.selectSubscription(subscriptionInput)
			})
		})

		this.onetimeInput?.addEventListener('change', () => {
			if (!this.onetimeInput?.checked) return
			this.selectOneTime()
		})

		this.sellingPlanToggles.forEach((toggle) => {
			toggle.addEventListener('click', () => {
				const select = toggle.closest<HTMLElement>(
					'.product-subscription__select'
				)
				if (!select) return

				const open = !select.classList.contains('is-open')
				this.closePlanMenus()
				select.classList.toggle('is-open', open)
				toggle.setAttribute('aria-expanded', String(open))
			})
		})

		this.sellingPlans.forEach((plan) => {
			plan.addEventListener('click', () => {
				this.resetPlans()
				this.setPlan(plan)
			})
		})

		this.initializeFromUrl()
		this.syncSelectedContainers()
	}

	initializeFromUrl() {
		if (!this.updatesUrl) return
		const requestedPlanId = new URL(window.location.href).searchParams.get(
			'selling_plan'
		)
		if (!requestedPlanId) {
			if (this.sellingPlanSelected.value) {
				replaceProductUrlParameters({
					selling_plan: this.sellingPlanSelected.value,
				})
			}
			return
		}

		const plan = Array.from(this.sellingPlans).find(
			(candidate) => candidate.dataset.sellingPlanId === requestedPlanId
		)
		if (!plan) {
			replaceProductUrlParameters({ selling_plan: null })
			return
		}

		const container = plan.closest<HTMLElement>(
			'.subscription-widget__container'
		)
		const subscriptionInput = q.os<HTMLInputElement>(
			'.subscription',
			container ?? undefined
		)
		if (!container || !subscriptionInput) {
			replaceProductUrlParameters({ selling_plan: null })
			return
		}

		if (this.onetimeInput) this.onetimeInput.checked = false
		subscriptionInput.checked = true
		this.removeActive()
		q.os<HTMLElement>(
			'.subscription-widget__content',
			container
		)?.classList.add('active')
		this.resetPlans()
		this.setPlan(plan)
	}

	selectSubscription(subscriptionInput: HTMLInputElement) {
		const container = subscriptionInput.closest<HTMLElement>(
			'.subscription-widget__container'
		)
		if (!container) return

		const wrapper = q.os<HTMLElement>(
			'.subscription-widget__content',
			container
		)
		this.removeActive()
		this.syncSelectedContainers()
		wrapper?.classList.add('active')
		this.resetPlans()

		const plan = q.os<HTMLElement>('.selling-plan', container)
		if (plan) this.setPlan(plan)
	}

	selectOneTime() {
		this.removeActive()
		this.syncSelectedContainers()
		this.resetPlans()
		this.sellingPlanSelected.value = ''
		this.sellingPlanSelected.dispatchEvent(
			new Event('change', { bubbles: true })
		)
		if (this.updatesUrl) {
			replaceProductUrlParameters({ selling_plan: null })
		}
	}

	resetPlans() {
		this.sellingPlans.forEach((plan) => {
			plan.classList.remove('active')
			plan.setAttribute('aria-pressed', 'false')
		})
		this.closePlanMenus()
	}

	setPlan(plan: HTMLElement) {
		plan.classList.add('active')
		plan.setAttribute('aria-pressed', 'true')
		const planId = plan.dataset.sellingPlanId
		if (!planId) return

		const container = plan.closest<HTMLElement>(
			'.subscription-widget__container'
		)
		const price = q.os<HTMLElement>(
			'[data-subscription-price]',
			container ?? undefined
		)
		const comparePrice = q.os<HTMLElement>(
			'[data-subscription-compare-price]',
			container ?? undefined
		)
		if (price && plan.dataset.sellingPlanPrice) {
			price.textContent = plan.dataset.sellingPlanPrice
		}
		if (comparePrice) {
			comparePrice.textContent = plan.dataset.sellingPlanComparePrice ?? ''
			comparePrice.toggleAttribute(
				'hidden',
				!plan.dataset.sellingPlanComparePrice
			)
		}
		const select = plan.closest<HTMLElement>(
			'.product-subscription__select'
		)
		const selectedLabel = q.os<HTMLElement>(
			'[data-selling-plan-selected-label]',
			select ?? undefined
		)
		if (selectedLabel) selectedLabel.textContent = plan.textContent?.trim() ?? ''
		this.sellingPlanSelected.value = planId
		this.sellingPlanSelected.dispatchEvent(
			new Event('change', { bubbles: true })
		)
		if (this.updatesUrl) {
			replaceProductUrlParameters({ selling_plan: planId })
		}
	}

	closePlanMenus() {
		this.sellingPlanSelects.forEach((select) => {
			select.classList.remove('is-open')
			q.os<HTMLButtonElement>(
				'.selling-plan-selected',
				select
			)?.setAttribute('aria-expanded', 'false')
		})
	}

	removeActive() {
		this.sellingPlanContents.forEach((content) => {
			content.classList.remove('active')
		})
	}

	syncSelectedContainers() {
		this.containers.forEach((container) => {
			const input = q.os<HTMLInputElement>(
				'.subscription_checkbox',
				container
			)
			container.classList.toggle('is-selected', Boolean(input?.checked))
		})
	}
}
